import { useState, useCallback, useEffect, useMemo } from "react";
import { intl, fetchUrl, fetchData, HTTP_STATUS } from "dmsbasereact";
import { DOCUMENT_REQUIREMENTS, documentRequiresExpiration } from "../../constants/DocumentRequirements";
import { InstitutionStatusEnum } from "../../constants/InstitutionStatus";
import { normalizeDocumentName } from "../../utils/documentTranslations";
import {
    DISTRIBUTOR_CATEGORY,
    REQUIRES_COMBINATION_CATEGORY,
    validateCategorySelection,
    wouldLeaveBuyerAlone,
    isForbiddenCategoryCombination,
    getAvailableCategoriesForUserType,
    getDefaultCategoriesForUserType,
    isCategorySelectLocked,
    hasDistributor,
    isCategoryLocked,
} from "../../constants/InstitutionType";

/**
 * Custom hook for managing institution setup form state.
 * Handles form data, file uploads, and form submission.
 *
 * @param {Object} options
 * @param {boolean} options.isVisible - Modal visibility
 * @param {Function} options.onSave - Save callback
 * @param {Object} options.institution - Institution data for edit mode
 * @param {string} options.mode - 'create', 'edit', or 'view'
 * @param {boolean} options.publicMode - Public mode for registration
 * @param {boolean} options.linkToUser - Auto-link to current user
 * @param {number[]} options.existingInstitutionPks - Existing institution PKs
 * @param {string|null} options.userType - User type from USER_TYPE_VALUES (for category filtering)
 */
export const useInstitutionSetupForm = ({
    isVisible,
    onSave,
    institution,
    mode,
    publicMode = false, // Public mode for registration (no auth required)
    linkToUser = false, // If true, auto-link created institution to current user
    existingInstitutionPks = [], // Existing institution pks to preserve when auto-linking
    userType = null, // User type for category filtering (null = admin flow with all options)
}) => {
    const isViewMode = mode === 'view';
    const isEditMode = mode === 'edit';
    const isCreateMode = mode === 'create';

    // Form data state
    const [formData, setFormData] = useState({
        institution_name: "",
        cnpj: "",
        is_public: false,
        institution_email: "",
        institution_phone: "",
        institution_zip_code: "",
        institution_city: "",
        institution_state: "",
        institution_address: "",
        institution_number: "",
    });

    // Files state: { docName: { file, expirationDate, existingFile, isExempt, exemptionNote } }
    const [files, setFiles] = useState({});
    // Multi-type support: array of category values
    const [institutionTypes, setInstitutionTypes] = useState([]);
    const [saving, setSaving] = useState(false);
    // Category validation error message
    const [categoryError, setCategoryError] = useState('');

    // Partner group state
    const [availableGroups, setAvailableGroups] = useState([]);
    const [selectedGroupPk, setSelectedGroupPk] = useState(undefined);
    const [loadingGroups, setLoadingGroups] = useState(false);

    // Category request state (for submit functionality)
    const [categoryRequests, setCategoryRequests] = useState([]);
    const [submittingCategoryRequest, setSubmittingCategoryRequest] = useState(false);

    // Get required documents based on institution types (union of all requirements)
    // Also includes documents for pending category requests so the user can
    // upload/edit them before or after submitting — including rejected requests
    // that need to be corrected and resubmitted (only APPROVED is excluded since
    // that category is already in institutionTypes).
    const requiredDocs = useMemo(() => {
        const pendingCategories = categoryRequests
            .filter(req => req.status !== InstitutionStatusEnum.APPROVED)
            .map(req => req.category);

        const allTypes = [...new Set([...institutionTypes, ...pendingCategories])];

        if (allTypes.length === 0) return [];

        const allDocs = new Set();
        for (const type of allTypes) {
            const reqs = DOCUMENT_REQUIREMENTS[type];
            if (reqs) {
                for (const doc of reqs) {
                    allDocs.add(doc);
                }
            }
        }
        return Array.from(allDocs);
    }, [institutionTypes, categoryRequests]);

    // Get available category options based on current selection
    // Get available category options based on current selection and user type
    const availableCategoryOptions = useMemo(() => {
        return getAvailableCategoriesForUserType(institutionTypes, userType);
    }, [institutionTypes, userType]);

    // Check if category select should be completely locked for this user type
    const isCategoryFieldLocked = useMemo(() => {
        return isCategorySelectLocked(userType);
    }, [userType]);

    // Fetch available partner groups
    const fetchPartnerGroups = useCallback(async () => {
        setLoadingGroups(true);
        try {
            const response = await fetchData("/institution_partner_group/all", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                const data = response.data?.data || response.data || [];
                setAvailableGroups(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error("Error fetching partner groups:", error);
            setAvailableGroups([]);
        } finally {
            setLoadingGroups(false);
        }
    }, []);

    // Fetch partner groups for institution
    const fetchInstitutionGroups = useCallback(async (orgBranchPk) => {
        if (!orgBranchPk) return;
        try {
            const response = await fetchData(
                `/institution_partner_group/by-institution/${orgBranchPk}`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                const groups = response.data?.data || response.data || [];
                // Select the first group if any
                if (groups.length > 0) {
                    setSelectedGroupPk(groups[0].pk);
                }
            }
        } catch (error) {
            console.error("Error fetching institution groups:", error);
        }
    }, []);

    // Fetch category requests for institution (for submit button visibility)
    const fetchCategoryRequests = useCallback(async (institutionPk) => {
        if (!institutionPk) return;
        try {
            const response = await fetchData(
                `/institution/${institutionPk}/categories`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                const pendingRequests = response.data?.data?.pending_requests || [];
                setCategoryRequests(pendingRequests);
            }
        } catch (error) {
            console.error("Error fetching category requests:", error);
            setCategoryRequests([]);
        }
    }, []);

    // Initialize form when modal opens or institution changes
    useEffect(() => {
        if (isVisible && institution) {
            const orgBranch = institution.organization_branch || {};
            const address = orgBranch.address || {};

            setFormData({
                institution_name: orgBranch.name || "",
                cnpj: orgBranch.cnpj || "",
                is_public: institution.is_public || false,
                institution_email: orgBranch.email_address || "",
                institution_phone: orgBranch.phone_number || "",
                institution_zip_code: address.zip_code || "",
                institution_city: address.city || "",
                institution_state: address.state || "",
                institution_address: address.address || "",
                institution_number: address.number || "",
            });
            // Support multi-type: check if institution has categories array
            if (institution.categories && Array.isArray(institution.categories) && institution.categories.length > 0) {
                setInstitutionTypes(institution.categories);
            } else if (orgBranch.category != null) {
                // Legacy single category support
                setInstitutionTypes([orgBranch.category]);
            } else {
                setInstitutionTypes([]);
            }

            // Load existing files
            if (institution.files && Array.isArray(institution.files)) {
                const existingFiles = {};
                institution.files.forEach(f => {
                    // Ensure file PK is preserved from either f.file.pk or f.file_pk
                    const filePk = f.file?.pk || f.file_pk;
                    // Normalize document name to canonical English key
                    // (handles DB records stored with translated names like "Alvará Sanitário" → "Sanitary Permit")
                    const docName = normalizeDocumentName(f.document_name);
                    existingFiles[docName] = {
                        file: null,
                        expirationDate: f.expiration_date || null,
                        existingFile: filePk ? {
                            pk: filePk,
                            original_filename: f.file?.original_filename || f.document_name
                        } : null,
                        isExempt: f.is_exempt || false,
                        exemptionNote: f.exemption_note || '',
                    };
                });
                setFiles(existingFiles);
            } else {
                setFiles({});
            }

            // Fetch partner groups for edit mode (skip in public mode)
            if (!publicMode) {
                fetchPartnerGroups();
                if (orgBranch.pk || orgBranch.thing_pk) {
                    fetchInstitutionGroups(orgBranch.pk || orgBranch.thing_pk);
                }
                // Fetch category requests for edit mode (to show submit button)
                if (institution.pk) {
                    fetchCategoryRequests(institution.pk);
                }
            }
        } else if (isVisible && !institution) {
            // Reset form for create mode
            setFormData({
                institution_name: "",
                cnpj: "",
                is_public: false,
                institution_email: "",
                institution_phone: "",
                institution_zip_code: "",
                institution_city: "",
                institution_state: "",
                institution_address: "",
                institution_number: "",
            });
            setFiles({});
            // Set default categories based on user type (e.g., Distributor+Buyer for DISTRIBUTOR_RESPONSIBLE)
            const defaultCategories = getDefaultCategoriesForUserType(userType);
            setInstitutionTypes(defaultCategories);
            setSelectedGroupPk(null);
            setCategoryError('');
            // Fetch partner groups only if not in public mode
            if (!publicMode) {
                fetchPartnerGroups();
            }
        }
    }, [isVisible, institution, fetchPartnerGroups, fetchInstitutionGroups, fetchCategoryRequests, publicMode, userType]);

    // Handle file selection with expiration date
    const handleFileSelect = useCallback((docName, file, expirationDate) => {
        setFiles(prev => {
            // If both file and expirationDate are null, user is removing the file
            if (file === null && expirationDate === null) {
                // Remove the entry completely or set all fields to null
                const newFiles = { ...prev };
                delete newFiles[docName];
                return newFiles;
            }

            // Otherwise, update the file data, preserving exemption state
            return {
                ...prev,
                [docName]: {
                    file,
                    expirationDate,
                    existingFile: prev[docName]?.existingFile || null,
                    isExempt: prev[docName]?.isExempt || false,
                    exemptionNote: prev[docName]?.exemptionNote || '',
                },
            };
        });
    }, []);

    // Handle exemption change
    const handleExemptionChange = useCallback((docName, isExempt, exemptionNote) => {
        setFiles(prev => ({
            ...prev,
            [docName]: {
                ...prev[docName],
                isExempt,
                exemptionNote,
                // Clear expiration date when exempt is set
                expirationDate: isExempt ? null : prev[docName]?.expirationDate,
            },
        }));
    }, []);

    // Handle form field changes
    const handleFormChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    // Handle institution type change (supports both single and multi-select)
    const handleInstitutionTypeChange = useCallback((value) => {
        setCategoryError('');

        // If value is an array (multi-select), set directly
        if (Array.isArray(value)) {
            // If Distributor is in the array, ensure Buyer is also included
            if (value.includes(DISTRIBUTOR_CATEGORY)) {
                // Distributor + Buyer only, no other combinations
                setInstitutionTypes([DISTRIBUTOR_CATEGORY, REQUIRES_COMBINATION_CATEGORY]);
                return;
            }
            // If only Buyer remains, clear selection (Buyer cannot be alone)
            if (value.length === 1 && value[0] === REQUIRES_COMBINATION_CATEGORY) {
                setCategoryError(intl.get('BuyerCannotBeAlone').d('Sales Channel must be combined with at least one other category'));
                setInstitutionTypes([]);
                return;
            }
            // Check if the resulting combination is forbidden (e.g. Buyer + Prescriber alone)
            const forbiddenCheck = isForbiddenCategoryCombination(value);
            if (forbiddenCheck.forbidden) {
                setCategoryError(intl.get('ForbiddenCategoryCombination').d(forbiddenCheck.message));
                return;
            }
            setInstitutionTypes(value);
            return;
        }

        // Single value - toggle behavior
        if (value == null) {
            setInstitutionTypes([]);
            return;
        }

        setInstitutionTypes(prev => {
            // If already selected, try to remove it
            if (prev.includes(value)) {
                // Check if category is locked (Buyer when Distributor is selected)
                if (isCategoryLocked(prev, value)) {
                    setCategoryError(intl.get('BuyerLockedWithDistributor').d('Sales Channel is required when Distributor is selected'));
                    return prev;
                }

                // If removing Distributor, also remove Buyer
                if (value === DISTRIBUTOR_CATEGORY) {
                    return [];
                }

                // Check if removing would leave Buyer alone
                if (wouldLeaveBuyerAlone(prev, value)) {
                    setCategoryError(intl.get('BuyerCannotBeAlone').d('Sales Channel must be combined with at least one other category'));
                    return prev;
                }

                return prev.filter(v => v !== value);
            }

            // Adding a new category
            // If adding Distributor, auto-select Buyer and set only these two
            if (value === DISTRIBUTOR_CATEGORY) {
                // Check if current selection has categories other than Buyer
                const otherCategories = prev.filter(c => c !== REQUIRES_COMBINATION_CATEGORY);
                if (otherCategories.length > 0) {
                    setCategoryError(intl.get('DistributorOnlyWithBuyer').d('Distributor can only be combined with Sales Channel'));
                    return prev;
                }
                // Set Distributor + Buyer (locked pair)
                return [DISTRIBUTOR_CATEGORY, REQUIRES_COMBINATION_CATEGORY];
            }

            // Validate the new selection
            const validation = validateCategorySelection(prev, value);
            if (!validation.valid) {
                // Use appropriate translation key based on the validation message
                let errorMessage;
                if (validation.message.includes('only be combined with Sales Channel')) {
                    errorMessage = intl.get('DistributorOnlyWithBuyer').d('Distributor can only be combined with Sales Channel');
                } else if (validation.message.includes('cannot be combined with')) {
                    errorMessage = intl.get('ForbiddenCategoryCombination').d(validation.message);
                } else {
                    errorMessage = validation.message;
                }
                setCategoryError(errorMessage);
                return prev;
            }

            // Add the new category
            return [...prev, value];
        });
    }, []);

    // Handle partner group change
    const handlePartnerGroupChange = useCallback((value) => {
        setSelectedGroupPk(value);
    }, []);

    // Handle form submission
    const handleSubmit = useCallback(async () => {
        setSaving(true);
        try {
            // Upload new files first
            const files_to_link = [];

            // Use public endpoint for file upload in public mode
            const uploadEndpoint = publicMode ? `/institution/file_upload/public` : `/institution/file_upload`;

            for (const [docName, fileData] of Object.entries(files)) {
                if (fileData?.file) {
                    // Upload new file (handle_swal=false to avoid toast for each file)
                    const uploadRes = await fetchUrl(
                        uploadEndpoint,
                        'POST', !publicMode, fileData.file, false, false, null, null, 'multipart/form-data'
                    );

                    if (uploadRes.status === HTTP_STATUS.CREATED_201 && uploadRes.data?.data?.pk) {
                        files_to_link.push({
                            file_pk: uploadRes.data.data.pk,
                            document_name: docName,
                            expiration_date: fileData.isExempt ? null : (fileData.expirationDate || null),
                            is_exempt: fileData.isExempt || false,
                            exemption_note: fileData.isExempt ? (fileData.exemptionNote || null) : null,
                        });
                    }
                } else if (fileData?.existingFile) {
                    // Keep existing file with potentially updated expiration date/exemption
                    files_to_link.push({
                        file_pk: fileData.existingFile.pk,
                        document_name: docName,
                        expiration_date: fileData.isExempt ? null : (fileData.expirationDate || null),
                        is_exempt: fileData.isExempt || false,
                        exemption_note: fileData.isExempt ? (fileData.exemptionNote || null) : null,
                    });
                }
            }

            // Use first category for legacy organization_branch.category field
            // Full categories list is sent separately
            const primaryCategory = institutionTypes.length > 0 ? institutionTypes[0] : null;

            const payload = {
                is_public: formData.is_public,
                organization_branch: {
                    name: formData.institution_name,
                    cnpj: formData.cnpj || "",
                    category: primaryCategory,
                    email_address: formData.institution_email,
                    phone_number: formData.institution_phone?.replace(/\D/g, "") || null,
                    address: {
                        address_name: formData.cnpj || formData.institution_name,
                        address: formData.institution_address,
                        number: formData.institution_number,
                        zip_code: formData.institution_zip_code,
                        city: formData.institution_city,
                        state: formData.institution_state,
                        country_code: 'BR',
                    }
                },
                files: files_to_link,
                partner_group_pk: selectedGroupPk,
                // Multi-type support: send all selected categories
                categories: institutionTypes,
            };

            const result = await onSave(payload, institution?.pk);

            // Auto-link institution to current user if linkToUser is true and this is a create operation
            if (linkToUser && !institution?.pk && result?.organization_branch_pk) {
                try {
                    // Include existing institutions + new one to preserve all links
                    const allInstitutionPks = [...existingInstitutionPks, result.organization_branch_pk];
                    await fetchData("/person/organization_branches/sync", "PUT", true, {
                        organization_branch_pks: allInstitutionPks
                    });
                } catch (linkError) {
                    console.warn("Failed to link institution to user:", linkError);
                    // Don't fail the entire operation if linking fails
                }
            }

            return { success: true, data: result };
        } catch (error) {
            console.error("Error saving institution:", error);
            return { success: false, error: error?.message || "Error saving institution" };
        } finally {
            setSaving(false);
        }
    }, [formData, files, institutionTypes, institution, onSave, selectedGroupPk, publicMode, linkToUser, existingInstitutionPks]);

    // Handle submitting institution or category request for approval
    const handleSubmitForApproval = useCallback(async (submissionType, itemPk) => {
        if (!institution?.pk) return { success: false };

        setSubmittingCategoryRequest(true);
        try {
            let endpoint;
            if (submissionType === 'institution') {
                // New institution submit
                endpoint = `/admin/institutions/${institution.pk}/submit-for-approval`;
            } else {
                // Category request submit
                endpoint = `/institution/${institution.pk}/category-requests/${itemPk}/submit`;
            }

            const response = await fetchData(endpoint, 'POST', true);

            if (response.status === HTTP_STATUS.OK_200) {
                // Refresh category requests to update UI
                if (institution.pk && submissionType === 'category_request') {
                    await fetchCategoryRequests(institution.pk);
                }
                window.dispatchEvent(new Event('institutionStatusChanged'));
                return { success: true };
            } else {
                return {
                    success: false,
                    error: response.data?.detail || intl.get('ErrorSubmitting').d('Error submitting for approval')
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error?.data?.detail || intl.get('ErrorSubmitting').d('Error submitting for approval')
            };
        } finally {
            setSubmittingCategoryRequest(false);
        }
    }, [institution, fetchCategoryRequests]);

    // Get modal title based on mode
    const title = useMemo(() => {
        if (isViewMode) return intl.get("ViewInstitution").d("View Institution");
        if (isEditMode) return intl.get("EditInstitution").d("Edit Institution");
        return intl.get("CreateInstitution").d("Create Institution");
    }, [isViewMode, isEditMode]);

    // Validate form - check all required fields are filled
    const { isFormValid, validationErrors, fieldErrors } = useMemo(() => {
        const errors = [];

        // Required fields
        if (!formData.institution_name?.trim()) {
            errors.push({ field: "institution_name", message: intl.get("InstitutionNameRequired").d("Institution name is required") });
        }
        if (!formData.is_public && !formData.cnpj?.trim()) {
            errors.push({ field: "cnpj", message: intl.get("CnpjRequired").d("CNPJ is required") });
        }
        if (!formData.institution_email?.trim()) {
            errors.push({ field: "institution_email", message: intl.get("EmailRequired").d("Email is required") });
        }
        if (!formData.institution_phone?.trim()) {
            errors.push({ field: "institution_phone", message: intl.get("PhoneRequired").d("Phone is required") });
        }
        if (institutionTypes.length === 0) {
            errors.push({ field: "institutionTypes", message: intl.get("InstitutionTypeRequired").d("At least one institution type is required") });
        }

        // Required address fields
        if (!formData.institution_address?.trim()) {
            errors.push({ field: "institution_address", message: intl.get("AddressRequired").d("Address is required") });
        }
        if (!formData.institution_number?.trim()) {
            errors.push({ field: "institution_number", message: intl.get("NumberRequired").d("Number is required") });
        }
        if (!formData.institution_city?.trim()) {
            errors.push({ field: "institution_city", message: intl.get("CityRequired").d("City is required") });
        }
        if (!formData.institution_state?.trim()) {
            errors.push({ field: "institution_state", message: intl.get("StateRequired").d("State is required") });
        }
        if (!formData.institution_zip_code?.trim()) {
            errors.push({ field: "institution_zip_code", message: intl.get("ZipCodeRequired").d("Zip code is required") });
        }

        // Documents are optional, but if uploaded, expiration dates are required for CRT and Sanitary Permit
        // Also, if user filled expiration date or checked exemption, the document file is required
        Object.entries(files).forEach(([docName, docData]) => {
            const hasFile = !!(docData?.file || docData?.existingFile);
            const hasExpirationDate = !!docData?.expirationDate;
            const hasExemption = !!docData?.isExempt;

            if (hasFile && documentRequiresExpiration(docName) && !docData?.isExempt && !docData?.expirationDate) {
                errors.push({
                    field: `doc_${docName}`,
                    message: intl.get("ExpirationDateRequired").d("Expiration date is required for this document")
                });
            }

            // If user filled date or checked exemption but didn't upload a file, require the document
            if (!hasFile && (hasExpirationDate || hasExemption)) {
                errors.push({
                    field: `doc_${docName}`,
                    message: intl.get("DocumentFileRequired").d("Please upload the document file")
                });
            }
        });

        // Convert errors array to field map
        const errorMap = errors.reduce((acc, err) => {
            acc[err.field] = err.message;
            return acc;
        }, {});

        return {
            isFormValid: errors.length === 0,
            validationErrors: errors,
            fieldErrors: errorMap,
        };
    }, [formData, institutionTypes, files]);

    // Helper to check if a category is locked in current selection
    const checkCategoryLocked = useCallback((category) => {
        return isCategoryLocked(institutionTypes, category);
    }, [institutionTypes]);

    // Check if distributor is selected (for UI feedback)
    const isDistributorSelected = useMemo(() => {
        return hasDistributor(institutionTypes);
    }, [institutionTypes]);

    return {
        isViewMode,
        isEditMode,
        isCreateMode,
        formData,
        setFormData,
        handleFormChange,
        files,
        handleFileSelect,
        handleExemptionChange,
        requiredDocs,
        handleSubmit,
        // Multi-type support
        institutionTypes,
        handleInstitutionTypeChange,
        availableCategoryOptions,
        categoryError,
        checkCategoryLocked,
        isDistributorSelected,
        isCategoryFieldLocked, // True if user type has locked categories (e.g., Distributor Responsible, Prescriber)
        // Legacy compatibility (use first type)
        institutionType: institutionTypes.length > 0 ? institutionTypes[0] : null,
        title,
        saving,
        isFormValid,
        validationErrors,
        fieldErrors,
        // Partner group state
        availableGroups,
        selectedGroupPk,
        handlePartnerGroupChange,
        loadingGroups,
        // Category request state (for submit functionality)
        categoryRequests,
        submittingCategoryRequest,
        handleSubmitForApproval,
    };
};
